import { z } from "zod";

const UnitValueSchema = z.object({
  unit: z.string(),
  value: z.union([z.number(), z.literal("")]),
});
export type UnitValue = z.infer<typeof UnitValueSchema>;

const CalcFormSchema = z.object({
  renewableEnergy: UnitValueSchema,
  goPrice: UnitValueSchema,
  grayPrice: UnitValueSchema,
});
export type CalcForm = z.infer<typeof CalcFormSchema>;

/**
 * Inputs of both calculator endpoints, in API units: `target` in kWh per year,
 * prices per MWh in the plant's currency. Write-only, so never echoed back.
 */
export type CalcRequest = {
  target: number;
  go_price: number;
  gray_electricity_price: number;
};

/**
 * Response of `POST /public_relations/calculator` (`SimulatorDataPartial`), the
 * free results. Rates are fractions (1 = 100%); `irr` (after tax) and
 * `irr_before_taxes` (the producer's withholding grossed back up) are null when
 * they cannot be computed. `total_savings` = royalty (after tax) + GO value over
 * the contract.
 */
export const CalculatorResultSchema = z.object({
  total_savings: z.number(),
  energy_saving_percentage: z.number(),
  irr: z.number().nullable(),
  irr_before_taxes: z.number().nullable(),
});
export type CalculatorResult = z.infer<typeof CalculatorResultSchema>;

/**
 * A yearly series keyed `year_1` ... `year_n`, DEC Badger's convention. Order the
 * keys by their numeric N: as strings `year_10` sorts before `year_2`.
 */
const YearSeriesSchema = z.record(z.string(), z.number());

/**
 * Detailed results of `POST /public_relations/calculator/followup` (`SimulatorData`),
 * the email-gated calculation, as the API sends them. `years` maps each `year_N` key
 * to its calendar year; the first and last may be partial, so a mid-year start has
 * one more key than the rental period. Every series is keyed like `years`
 * (`annual_profits_sold_energy` is after tax); the cumulative-cost lines `red_line` /
 * `green_line` also carry `year_0` = 0, the starting point. Rates are fractions;
 * `break_even_year` is a calendar year, -1 if never reached, after tax;
 * `break_even_year_before_taxes` is the same on the pre-tax flows of `irr_before_taxes`,
 * never later, and `irr_before_taxes` the IRR of those flows (null when it cannot be
 * computed). Both are optional: an older API does not send them. Only the consumed
 * fields are typed.
 */
export const CalculatorDetailSchema = z.object({
  initial_investment: z.number(),
  irr: z.number().nullable(),
  irr_before_taxes: z.number().nullable().optional(),
  break_even_year: z.number(),
  break_even_year_before_taxes: z.number().optional(),
  years: YearSeriesSchema,
  annual_profits_sold_energy: YearSeriesSchema,
  annual_go_savings: YearSeriesSchema,
  annual_production: YearSeriesSchema,
  energy_prices: YearSeriesSchema,
  red_line: YearSeriesSchema,
  green_line: YearSeriesSchema,
});
export type CalculatorDetailResponse = z.infer<typeof CalculatorDetailSchema>;

/**
 * The followup as the explorer reads it (`fetchFollowup`): `years` and every series
 * as arrays in year order, so index `i` is calendar year `years[i]` everywhere, except
 * the lines: they lead with their `year_0` starting point, so they plot on `lineYears`.
 */
export type CalculatorDetail = {
  [K in keyof CalculatorDetailResponse]: CalculatorDetailResponse[K] extends Record<string, number>
    ? number[]
    : CalculatorDetailResponse[K];
};
